import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Icons from '~/Common-Objects/UI/Icons/index'
import Modal from '~/Common-Objects/Modal/index'
import ProgressNavSmall from '~/Common-Objects/Navigation/ProgressNavSmall'
import axios from '~/data/http/axios'
import EmailValidator from '~/Classes/emailValidator'
import Step1 from './step1'
import Step2 from './step2'
import Step3 from './step3'
import PostHogFunctions from '~/Classes/posthog'
import i18n from '~/i18n'

export default function NewUserQuestions({ onFinish = () => {}, userOnly = false }) {
    const [answers, setAnswers] = useState({})
    const [errors, setErrors] = useState({
        step1: {},
        step2: {},
        step3: {}
    })
    const [step, setStep] = useState(1)
    const [submitting, setSubmitting] = useState(false)
    const [roles, setRoles] = useState([])
    const user = useSelector((state) => state.user)
    const account = useSelector((state) => state.accountMaster)

    const next = async (skipEmail) => {
        const errs = await validate(skipEmail)
        setErrors({ ...errors, ['step' + step]: errs })
        if (Object.keys(errs).length > 0) return

        let phACtion = ''
        if (step === 1) phACtion = 'onboarding-1-button-continue'
        if (step === 2) phACtion = 'onboarding-2-button-continue'
        if (step === 3 && skipEmail) phACtion = 'onboarding-3-button-skip'
        if (step === 3 && !skipEmail) phACtion = 'onboarding-3-button-send-invite'
        if (phACtion) PostHogFunctions.capture(phACtion)

        if (userOnly) {
            submit()
        } else {
            let next = step + 1
            if (next === 4) {
                submit(skipEmail)
            } else {
                setStep(next)
            }
        }
    }

    const back = () => {
        let phACtion = ''
        if (step === 2) phACtion = 'onboarding-2-button-back'
        if (step === 3) phACtion = 'onboarding-3-button-back'
        if (phACtion) {
            PostHogFunctions.capture(phACtion)
        }
        setStep(step - 1)
    }

    const validate = async (skipEmail) => {
        const stepErrors = {}
        if (step === 1) {
            if (!answers.role) {
                stepErrors.role = 'Select your role'
            }
            if (!userOnly) {
                if (!answers.staffSize) {
                    stepErrors.staffSize = 'Select your team size'
                }
                if (!answers.contacts) {
                    stepErrors.contacts = 'Let us know how many contacts you manage'
                }
            } else {
                if (!answers.comfort) {
                    stepErrors.comfort = 'Choose the option that best matches your experience level'
                }
                if (!answers.importantToBusiness?.length) {
                    stepErrors.importantToBusiness = 'Choose at least one option'
                }
            }
        } else if (step === 2) {
            if (!answers.comfort) {
                stepErrors.comfort = 'Choose the option that best matches your experience level'
            }
            if (!answers.importantToBusiness?.length) {
                stepErrors.importantToBusiness = 'Choose at least one option'
            }
        } else if (step === 3 && !skipEmail) {
            // if there is an email address then validate it
            if (answers.inviteEmail) {
                if (!EmailValidator.validateEmail(answers.inviteEmail)) {
                    stepErrors.inviteEmail = 'Email is invalid'
                }
                if (!Object.keys(stepErrors).length) {
                    const data = { emailAddress: answers.inviteEmail, RoleId: answers.inviteRole }
                    try {
                        await axios.post('/accountMaster/register', data)
                        PostHogFunctions.capture('added_user')
                        PostHogFunctions.capture('added_user_onboarding_questions')
                    } catch (e) {
                        if (e && e.data && e.data.error && e.data.error.tierName) {
                            stepErrors.inviteEmail = e.data.error
                        } else {
                            stepErrors.inviteEmail = 'Error sending invite'
                        }
                    }
                }
            } else {
                stepErrors.inviteEmail = 'Email is required'
            }
        }
        return stepErrors
    }

    const submit = async () => {
        setSubmitting(true)
        const body = { ...answers }
        delete body.inviteEmail
        delete body.inviteRole
        if (userOnly) {
            body.userOnly = true
        }
        await axios.post('/accountMaster/questions', {
            answers: body
        })
        PostHogFunctions.ident(user, account)
        setSubmitting(false)
        onFinish()
    }

    useEffect(() => {
        axios.get('/role').then((res) => {
            setRoles(res.data.Roles.map((role) => ({ label: role.name, value: role.id })))
            setAnswers({ ...answers, inviteRole: res.data.Roles[0].id })
        })
    }, [])

    const continueDataAttribute = {}
    if (step === 1)
        continueDataAttribute.ph = userOnly
            ? 'new-user-onboarding-1-button-continue'
            : 'account-onboarding-1-button-continue'
    if (step === 2) continueDataAttribute.ph = 'account-onboarding-2-button-continue'
    if (step === 3) continueDataAttribute.ph = 'account-onboarding-3-button-send-invite'

    const backDataAttribute = {}
    if (step === 2) backDataAttribute.ph = 'account-onboarding-2-button-back'
    if (step === 3) backDataAttribute.ph = 'account-onboarding-3-button-back'

    return (
        <Modal
            noClickAway={true}
            blurBg={true}
            width={540}
            hideScroll={true}
            noScroll={true}
            top={true}
            footer={
                <div className='quickFlex' style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        {step > 1 && (
                            <Button
                                className='button-lightGrey'
                                iconLeft={<Icons icon='arrowLeft' />}
                                onClick={back}
                            >
                                Back
                            </Button>
                        )}
                    </div>
                    <div className='quickFlex' style={{ alignItems: 'center', gap: 8 }}>
                        {step === 3 && (
                            <Button
                                dataAttribute={{
                                    ph: 'onboarding-3-button-skip'
                                }}
                                className='button-ghost'
                                disabled={submitting}
                                onClick={() => next(true)}
                            >
                                {i18n.t('account:questions.skip')}
                            </Button>
                        )}
                        <Button
                            className='button-primary'
                            iconRight={<Icons icon='arrowRight' />}
                            onClick={() => next(false)}
                            disabled={submitting}
                            dataAttribute={continueDataAttribute}
                        >
                            {step < 3 ? i18n.t('account:questions.continue') : i18n.t('account:questions.send')}
                        </Button>
                    </div>
                </div>
            }
        >
            {!userOnly && <ProgressNavSmall steps={3} step={step} />}
            {step === 1 && (
                <Step1 answers={answers} setAnswers={setAnswers} errors={errors.step1} userOnly={userOnly} />
            )}
            {step === 2 && !userOnly && <Step2 answers={answers} setAnswers={setAnswers} errors={errors.step2} />}
            {step === 3 && !userOnly && (
                <Step3 answers={answers} setAnswers={setAnswers} errors={errors.step3} roles={roles} />
            )}
        </Modal>
    )
}
